import {makeAutoObservable} from "mobx";
import {apiRequest} from "../util/util";
import {StorySection} from "./storySection";
import track from "../util/frontend_analytics";
import {CastMember} from "./characters";

const PICTURE_POLL_MS = 3000;
const PICTURE_WAIT_MS = 2 * 60 * 1000;  // a picture still not drawn by then is shown as failed, with Try again

export class Story {
    theme: string | undefined
    id: string | undefined
    sections: StorySection[]
    ended: boolean
    processing: boolean
    underEdit: boolean
    owned: boolean
    watchingPictures: boolean
    pictureEdits: number  // counts edits, so a check that started before one can't show the old text's picture
    characters: CastMember[]  // the story's cast, as the engine snapshots them

    constructor() {
        this.sections = []
        this.ended = false
        this.processing = false
        this.underEdit = false
        this.owned = false
        this.watchingPictures = false
        this.pictureEdits = 0
        this.characters = []
        makeAutoObservable(this)
    }

    // Check back while some section's picture is still being drawn, then show each one as it arrives
    async watchPictures() {
        if (this.watchingPictures) {
            return
        }
        this.watchingPictures = true
        const startedAt = Date.now()
        try {
            while (this.sections.some((section) => section.picture === 'pending')) {
                if (Date.now() - startedAt > PICTURE_WAIT_MS) {
                    this.sections.filter((section) => section.picture === 'pending')
                        .forEach((section) => section.setPicture('failed', undefined))
                    break
                }
                await new Promise((resolve) => setTimeout(resolve, PICTURE_POLL_MS))
                const storyId = this.id
                const edits = this.pictureEdits
                let data
                try {
                    data = await apiRequest('ml', 'POST', {target: 'pictures', sessionId: storyId})
                } catch (e) {
                    continue  // a failed check is tried again until the wait runs out
                }
                if (!data || !data.success || storyId !== this.id || edits !== this.pictureEdits) {
                    continue
                }
                for (const update of data.sections) {
                    const section = this.sections.find((s) => s.id === update.sectionId)
                    if (section && section.picture === 'pending' && update.picture !== 'pending') {
                        section.setPicture(update.picture, update.imgUrl)
                    }
                }
            }
        } finally {
            this.watchingPictures = false
        }
    }

    setProcessing(processing: boolean) {
        this.processing = processing
    }

    setUnderEdit(underEdit: boolean) {
        this.underEdit = underEdit
    }

    async addSection(section: StorySection) {

        this.sections.push(section)
        // if the section doesn't have "images" property yet, generate new images
        if (section.images.length === 0) {
            const sessionId = this.id;
            const data = await apiRequest('ml', 'POST', {
                target: 'paint',
                sessionId: sessionId,
                sectionId: section.id
            })

            // make a get request to get the images, result is a json with a list of images
            // check if "src" is in data. If not, assume the data is already a list of images
            var images;

            if (data.lexicaUrl.src) {
                images = [data.lexicaUrl]
            } else {
                const searchUrl = encodeURI(data.lexicaUrl)
                // make a get request on searchURL
                const res = await fetch(searchUrl);
                const resJson = await res.json()
                images = resJson.images
            }


            section.setImages(images)
        }
    }

    noteEdit() {
        this.pictureEdits += 1
    }

    setId(id: string) {
        this.id = id
        // put the story id in cookie
        window.sessionStorage.setItem('storyId', id)

    }

    setOwned(owned: boolean) {
        this.owned = owned
    }

    setTheme(theme: string) {
        this.theme = theme;
    }

    setCharacters(characters: CastMember[] | undefined) {
        this.characters = Array.isArray(characters) ? characters : []
    }

    deleteLastSection() {

        this.sections.pop()
    }

    setEnded() {
        this.ended = true
    }


    clear() {

        this.sections = []
        this.id = undefined;
        this.characters = []
        window.sessionStorage.removeItem('storyId')
    }

    async generateNewSection(userText: string,
                             sectionId: string,
                             retry: Boolean = false,
                             quality: string = "low",
                             characterIds: string[] = []) {

        const data = await apiRequest('ml', 'POST', {
            target: 'write',
            sessionId: this.id,
            userText: userText,
            sectionId: sectionId,
            retry: retry,
            llama: true,
            modelName:'llama-7b',
            theme: this.theme,
            quality: quality,
            ...(characterIds.length > 0 ? {characterIds} : {})

        })
        // true once the new part is in; otherwise the reason, to show the reader
        if (data === null) {
            return "Couldn't continue the story. Please try again."
        }
        if (data.success === false || !data.text) {
            console.log(data.message)
            return data.message || "Couldn't continue the story. Please try again."
        }
        this.setId(data.sessionId)
        if (Array.isArray(data.characters)) {
            this.setCharacters(data.characters)
        }
        await this.addSection(new StorySection(data.sectionId,
            data.text,
            data.imgUrl,
            [data.imgUrl],
            data.endOfStory,
            data.userText,
            data.picture
        ))
        // continuing someone else's story makes a copy whose sections have new IDs
        if (Array.isArray(data.sectionIds) && data.sectionIds.length === this.sections.length) {
            data.sectionIds.forEach((id: string, i: number) => this.sections[i].setId(id))
        }
        if (data.picture === 'pending') {
            this.watchPictures()
        }
        if (data.endOfStory) {
            this.setEnded()
        }
        // @ts-ignore
        track("story_continue", {theme: this.theme, branch: process.env.BRANCH})
        return true;
    }

    getLastSection() {
        if (this.sections.length === 0) {
            return null
        }
        return this.sections[this.sections.length - 1]
    }

    getTentativeNextText(): string {
        return "Write the next few sentences here"
    }


}

const story = new Story()
export {story};